import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { 
  MagnifyingGlassIcon, 
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="hidden md:block bg-[#0B1F3A] text-blue-100 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-9">
          <div className="flex items-center space-x-5">
            <a href="tel:+496774420901" className="flex items-center space-x-1.5 hover:text-white transition-colors">
              <PhoneIcon className="h-3.5 w-3.5" />
              <span>Berlin HQ: +49 677 442 0901</span>
            </a>
            <a href="tel:+12134774806" className="flex items-center space-x-1.5 hover:text-white transition-colors">
              <PhoneIcon className="h-3.5 w-3.5" />
              <span>USA: +1 213-477-4806</span>
            </a>
            <a href="mailto:support@dutchcargoexpress.com" className="flex items-center space-x-1.5 hover:text-white transition-colors">
              <EnvelopeIcon className="h-3.5 w-3.5" />
              <span>support@dutchcargoexpress.com</span>
            </a>
          </div>
            <div className="flex items-center space-x-1.5">
            <LanguageSelector variant="dark" />
            <GlobeAltIcon className="h-3.5 w-3.5" />
            <span>{t.nav.shippingWorldwide}</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
              <img 
                src="/logo-mark.svg" 
                alt="DutchCargoExpress" 
                className="h-8 w-8 sm:h-10 sm:w-10 md:h-11 md:w-11 transition-transform group-hover:scale-105"
              />
              <div className="leading-none">
                <span className="text-base sm:text-lg md:text-xl font-extrabold text-[#0B1F3A] block tracking-tight">
                  DutchCargo<span className="text-[#FF6B00]">Express</span>
                </span>
                <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 block mt-0.5 tracking-wide uppercase">Global Logistics Solutions</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <Link
                to="/"
                className={`${
                  isActive('/') 
                    ? 'text-[#FF6B00] bg-orange-50' 
                    : 'text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50'
                } px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200`}
              >
                {t.nav.home}
              </Link>
              <Link
                to="/track"
                className={`${
                  isActive('/track') 
                    ? 'text-[#FF6B00] bg-orange-50' 
                    : 'text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50'
                } px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-1.5`}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                <span>{t.nav.track}</span>
              </Link>
              <Link
                to="/contact"
                className={`${
                  isActive('/contact') 
                    ? 'text-[#FF6B00] bg-orange-50' 
                    : 'text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50'
                } px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200`}
              >
                {t.nav.contact}
              </Link>
              <LanguageSelector />
            </div>

            {/* Admin Section */}
            <div className="hidden md:flex items-center space-x-3">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/admin"
                    className="text-gray-600 hover:text-[#FF6B00] px-3 py-2 text-sm font-medium transition-colors"
                  >
                    {t.nav.dashboard}
                  </Link>
                  <div className="flex items-center space-x-1.5 text-sm text-gray-500">
                    <UserIcon className="h-4 w-4" />
                    <span>{user?.fullName || user?.username}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-[#0B1F3A] border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t.nav.logout}
                  </button>
                </div>
              ) : (
                <Link
                  to="/admin/login"
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#FF6B00] hover:bg-[#e65f00] rounded-lg transition-colors"
                >
                  {t.nav.adminLogin}
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-[#17202A] hover:text-[#FF6B00] focus:outline-none p-2 rounded-lg hover:bg-orange-50 transition-colors"
              >
                {isMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-3 pt-3 pb-4 space-y-1">
              <Link
                to="/"
                className={`${
                  isActive('/') 
                    ? 'text-[#FF6B00] bg-orange-50' 
                    : 'text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50'
                } block px-4 py-3 text-base font-medium rounded-lg transition-colors`}
                onClick={() => setIsMenuOpen(false)}
              >
                {t.nav.home}
              </Link>
              <Link
                to="/track"
                className={`${
                  isActive('/track') 
                    ? 'text-[#FF6B00] bg-orange-50' 
                    : 'text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50'
                } block px-4 py-3 text-base font-medium rounded-lg transition-colors`}
                onClick={() => setIsMenuOpen(false)}
              >
                {t.nav.trackPackage}
              </Link>
              <Link
                to="/contact"
                className={`${
                  isActive('/contact') 
                    ? 'text-[#FF6B00] bg-orange-50' 
                    : 'text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50'
                } block px-4 py-3 text-base font-medium rounded-lg transition-colors`}
                onClick={() => setIsMenuOpen(false)}
              >
                {t.nav.contact}
              </Link>
              <div className="px-4 py-2">
                <LanguageSelector />
              </div>
              
              {/* Mobile contact info */}
              <div className="pt-3 pb-2 border-t border-gray-100 space-y-1.5">
                <a href="tel:+496774420901" className="flex items-center space-x-2 px-4 py-2 text-sm text-[#64748B]">
                  <PhoneIcon className="h-4 w-4 text-[#FF6B00] flex-shrink-0" />
                  <span>Berlin HQ: +49 677 442 0901</span>
                </a>
                <a href="tel:+12134774806" className="flex items-center space-x-2 px-4 py-2 text-sm text-[#64748B]">
                  <PhoneIcon className="h-4 w-4 text-[#FF6B00] flex-shrink-0" />
                  <span>USA: +1 213-477-4806</span>
                </a>
                <a href="mailto:support@dutchcargoexpress.com" className="flex items-center space-x-2 px-4 py-2 text-sm text-[#64748B] break-all">
                  <EnvelopeIcon className="h-4 w-4 text-[#FF6B00] flex-shrink-0" />
                  <span>support@dutchcargoexpress.com</span>
                </a>
              </div>

              <div className="border-t border-gray-100 pt-3">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/admin"
                      className="block px-4 py-3 text-base font-medium text-[#17202A] hover:text-[#FF6B00] hover:bg-orange-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t.nav.dashboard}
                    </Link>
                    <div className="px-4 py-2 text-sm text-gray-500 flex items-center space-x-1.5">
                      <UserIcon className="h-4 w-4" />
                      <span>{user?.fullName || user?.username}</span>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-base font-medium text-[#DC2626] hover:bg-red-50 rounded-lg transition-colors"
                    >
                      {t.nav.logout}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/admin/login"
                    className="block px-4 py-3 text-base font-medium text-white bg-[#FF6B00] hover:bg-[#e65f00] rounded-lg text-center transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t.nav.adminLogin}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
